import { FC, useMemo, useState, useEffect, useRef, ChangeEvent } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { useForm, Controller, FieldError } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Button,
  Modal,
  Heading,
  Link,
  Types,
  InlineMessages,
  InlineMessagesType,
  RichText,
} from '@marriott/mi-ui-library';
import {
  ReslinkType,
  getReslinkEventByIdForModifyEventDttQuery,
  SubmitReslinkDttResponse,
  submitReslinkDttMutation,
  ImageType,
  ReslinkEventByIdDttResponse,
  HotelGroupEventDetails,
  getReslinkPickupStatsAlertSubscriptionsDttQuery,
  updateReslinkPickupStatsAlertSubscriptionDttMutation,
  PickupStatsAlertSubscriberInput,
  PickupStatsAlertEventProduct,
  PickupStatsAlertStatus,
  GetReslinkPickupStatsAlertSubscriptionsResponse,
  createReslinkPickupStatsAlertSubscriptionDttMutation,
  CreateReslinkPickupStatsAlertSubscriptionResponse,
  CreateReslinkPickupStatsAlertSubscriptionInput,
  deleteReslinkPickupStatsAlertSubscriptionDttMutation,
  DeleteReslinkPickupStatsAlertSubscriptionResponse,
  DeleteReslinkPickupStatsAlertSubscriptionInput,
  UpdateReslinkPickupStatsAlertSubscriptionInput,
  UpdateReslinkPickupStatsAlertSubscriptionResponse,
  PickupStatsAlertSubscription,
} from '@marriott/mi-groups-graphql';
import { Dropdown, TextFormField, CheckBoxFormField } from '../../../../molecules';
import {
  ReslinkEventDetailsFormData,
  ReslinkEventDetailsProps,
  DomainOption,
  ReslinkProductType,
  ReslinkPropertyCard,
} from './ReslinkEventDetails.types';
import { StyledReslinkEventDetails } from './ReslinkEventDetails.styles';
import { ImageTray } from './ImageTray';
import { AddHotelDtt } from './AddHotel';
import { ReservationLinkType } from '../DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';
import { useLocaleStore, useHeaderFieldsStore, useAuthStore, useDttStore } from '../../../../store';
import {
  buildPickupAlertSubscriptionDetailsByKey,
  buildPickupAlertIdentityKey,
  getCreateReslinkEventDttInput,
  getEventTrackingDataDtt,
  getPropertyDataOnModifyReslink,
  isGroupType,
} from '../../../utils/reslink';
import { UpdateEventStateModal, TextEditor } from '../../../molecules';
import { UpdateEventStateType } from '../../../molecules/UpdateEventStateModal/UpdateEventStateModal.types';
import { useReslinkAssociateStore } from '../../../store';
import { createCustomXRequestId, isPastDate, formatUTCDateString } from '../../../../utils';
import { PROPERTY_IMAGE_LIST_CATEGORY_LIMIT, EMAIL_REGEX, DEFAULT_LANG } from '../../../../constants';
import { EventTracking } from './EventTracking';
import { PickupStatsAlerts } from './PickupStatsAlerts';
import { AlertsData, PickupAlertQueryStatus } from './PickupStatsAlerts/PickupStatsAlerts.types';

export const ReslinkEventDetailsConfig = {
  emptyLabel: 'ReslinkModifyEventDetails',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/reslink/eventdetails`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ReslinkEventDetailsDtt: FC<{ model: ReslinkEventDetailsProps }> = (props: any) => {
  const {
    heading,
    selectDomain,
    domains,
    addAssociates,
    imageTray,
    optionalProperties,
    reslinkAddHotelsModel,
    cta,
    backPopOver,
    disableEventModal,
    errorInformationMessage,
    maxRoomCardErrorDtt,
    pastDateErrorDtt,
    invalidPagePath,
    apiFailure,
    eventTracking,
    editorProperties,
  } = props.model;
  const {
    heading: optionalHeading,
    groupOrEventLabel,
    groupOrEventPlaceholder,
    groupOrEventHelperText,
    groupOrEventRequiredError,
    companyOrOrganizationLabel,
    companyOrOrganizationPlaceholder,
    companyOrOrganizationHelperText,
    companyOrOrganizationRequiredError,
    informationLabel,
    informationPlaceholder,
    boldAriaLabel,
    italicAriaLabel,
    eventLink1Label,
    eventLink1Placeholder,
    eventLink1RequiredError,
    eventLink1Name,
    eventLink1NamePlaceholder,
    eventLink1NameHelperText,
    eventLink1NameRequiredError,
    eventLink2Label,
    eventLink2Placeholder,
    eventLink2RequiredError,
    eventLink2Name,
    eventLink2NamePlaceholder,
    eventLink2NameHelperText,
    eventLink2NameRequiredError,
    linkErrorMessage,
  } = optionalProperties;
  const { termsAndConditionPopUp, requiredError } = reslinkAddHotelsModel;
  const { heading: backModalHeading, stepContent, yesCtaLabel, cancelCtaLabel, backToRoomingListHubPath } = backPopOver;
  const {
    heading: eventTrackingHeading,
    isEnabledForDtt: isPickUpStatsEnabledAem,
    eventBookingsDetail,
    groupRates,
    emailNotification,
    pickupStatsCardLabels,
  } = eventTracking;

  const router = useRouter();
  const { eid } = useAuthStore();
  const { locale } = useLocaleStore();
  const { requestId } = useHeaderFieldsStore();
  const { isBranchIoAppDownloadEnabled, isPickupStatEventDetailsEnabled, isPickupStatsAlertsEnabled } = useDttStore();
  const isPickUpStatsEnabled = isPickUpStatsEnabledAem || isPickupStatEventDetailsEnabled;
  const {
    isSuperUser,
    PUSAEnabled,
    setEmailLocale,
    setEventTrackingData,
    setEventTrackingPropertyIndex,
    resetEventTrackingData,
    eventTrackingData,
    resetPropertyImages,
    addOriginalHotelIdentityKey,
  } = useReslinkAssociateStore();

  const flow = (router.query['flow'] as string) || '';
  const eventId = (router.query['id'] as string) || '';
  const reslinkType = (router.query['reslinkType'] as string) || '';

  const [alertState, setAlertState] = useState<{ view: string; propertyIndex: number }>(() => ({
    view: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') ?? '' : '',
    propertyIndex:
      typeof window !== 'undefined' ? Number(new URLSearchParams(window.location.search).get('propertyIndex')) || 0 : 0,
  }));
  const view = alertState.view;
  const alertPropertyIndex = alertState.propertyIndex;
  const showPickupStatsCards = locale?.toLowerCase() === DEFAULT_LANG.toLowerCase();
  const isPickupStatsAlertsAllowed = isPickupStatsAlertsEnabled || PUSAEnabled;
  const isAlertsView = showPickupStatsCards && view === 'alerts';

  const isGroup = isGroupType(reslinkType as ReservationLinkType);

  const domainOptions = useMemo(
    () => domains.map((domain: DomainOption) => ({ label: domain.label, value: domain.code, locale: domain.locale })),
    [domains]
  );
  const defaultDomain = useMemo(() => domains.find((domain: DomainOption) => domain.selected) || domains[0], [domains]);

  const [isValid, setIsValid] = useState<boolean>(false);
  const [eventDescriptionModified, setEventDescriptionModified] = useState<boolean>(false);
  const [showBackToHubModal, setShowBackToHubModal] = useState<boolean>(false);
  const [showDisableEventModal, setShowDisableEventModal] = useState<boolean>(false);
  const [showTncModal, setShowTncModal] = useState<boolean>(false);
  const [images, setImages] = useState<ImageType[]>([]);
  const [uploadImageError, setUploadImageError] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [syncedPickupStatsAlertSubscriptions, setSyncedPickupStatsAlertSubscriptions] = useState<
    Record<string, PickupStatsAlertSubscription>
  >({});

  const backButtonRef = useRef<HTMLAnchorElement | null>(null);
  const pickupStatsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEmailLocale(locale);

    return () => {
      resetPropertyImages();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetEventTrackingData();
  }, [locale]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const targetUrl = new URL(url, window.location.origin);
      const targetView = targetUrl.searchParams.get('view') || '';
      const targetPropertyIndex = Number(targetUrl.searchParams.get('propertyIndex')) || 0;

      const [currentBasePath] = router.asPath.split('?');

      if (targetUrl.pathname === currentBasePath) {
        setAlertState({
          view: targetView,
          propertyIndex: targetPropertyIndex,
        });

        throw 'AbortChange';
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.asPath]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      setAlertState({
        view: params.get('view') || '',
        propertyIndex: Number(params.get('propertyIndex')) || 0,
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorReason = event.reason;

      const isAbortError =
        (typeof errorReason === 'string' && errorReason.includes('AbortChange')) ||
        (errorReason && typeof errorReason === 'object' && errorReason.message?.includes('AbortChange'));

      if (isAbortError) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const {
    data: eventData,
    loading,
    error: getEventByIdError,
  } = useQuery<ReslinkEventByIdDttResponse>(getReslinkEventByIdForModifyEventDttQuery, {
    variables: {
      input: { eventId, reslinkType: ReslinkType[reslinkType as keyof typeof ReslinkType] ?? ReslinkType.NONE },
      groupReslink: isGroup,
      imageLimitPerCategory: PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
    },
    context: {
      headers: {
        'accept-language': locale,
        'uxl-cache-disable': true,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !eventId,
  });

  const { data: pickupStatsAlertSubscriptionsData, error: pickupStatsAlertSubscriptionsError } =
    useQuery<GetReslinkPickupStatsAlertSubscriptionsResponse>(getReslinkPickupStatsAlertSubscriptionsDttQuery, {
      variables: {
        input: {
          eventId: `${eventId}_GRP`,
        },
      },
      context: {
        headers: {
          'accept-language': locale,
          'uxl-cache-disable': true,
        },
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      skip: !eventId || !isPickupStatsAlertsAllowed || !isGroup || !showPickupStatsCards,
    });

  const pickupStatsAlertSubscriptionsResponse =
    pickupStatsAlertSubscriptionsData?.sales?.groupSales?.groupManagement?.reslinkPickupStatsAlertSubscriptions;

  const responseHasError = !!pickupStatsAlertSubscriptionsResponse && 'errors' in pickupStatsAlertSubscriptionsResponse;

  const pickupAlertQueryStatus: PickupAlertQueryStatus = {
    hasData: !!pickupStatsAlertSubscriptionsData && !responseHasError,
    hasError: !!pickupStatsAlertSubscriptionsError || responseHasError,
  };

  useEffect(() => {
    if (pickupStatsAlertSubscriptionsResponse && 'subscriptions' in pickupStatsAlertSubscriptionsResponse) {
      setSyncedPickupStatsAlertSubscriptions(
        buildPickupAlertSubscriptionDetailsByKey(pickupStatsAlertSubscriptionsResponse.subscriptions)
      );
    }
  }, [pickupStatsAlertSubscriptionsData]);

  useEffect(() => {
    if (eventData) {
      const { reslinkEventByIdWithInventory } = eventData?.sales?.groupSales || {};

      const {
        associatedUsers,
        createdBy,
        eventName,
        customImageURLs,
        eventDescription,
        eventCustomLinks,
        hotelGroupEventDetails,
      } = reslinkEventByIdWithInventory;

      const isAssociatedEvent =
        isSuperUser ||
        createdBy?.toLowerCase() === eid.toLowerCase() ||
        associatedUsers?.some(user => user.toLowerCase() === eid.toLowerCase());

      if (reslinkEventByIdWithInventory?.isActive && isAssociatedEvent) {
        const propertyCards: ReslinkPropertyCard[] = [];
        setIsValid(true);

        setValue('associates', associatedUsers?.join(';') as string);
        setValue('eventName', eventName);
        setImages(customImageURLs);
        setValue(
          'customImages',
          customImageURLs.map(({ name, link }) => ({ name, link }))
        );
        setValue('eventDescription', eventDescription);
        const userCustomLinks = eventCustomLinks?.filter(link => link.name !== '__branchIoType') || [];
        setValue('eventLinkName1', userCustomLinks?.[0]?.name || '');
        setValue('eventLink1', userCustomLinks?.[0]?.link);
        setValue('eventLinkName2', userCustomLinks?.[1]?.name || '');
        setValue('eventLink2', userCustomLinks?.[1]?.link);

        hotelGroupEventDetails.forEach((property: HotelGroupEventDetails, index: number) => {
          const propertyCard = getPropertyDataOnModifyReslink(property, reslinkAddHotelsModel.propertyCard, index + 1);
          propertyCards.push(propertyCard);

          if (isGroup) {
            const hasPastDateError =
              !property.groupEventDetails.groupBlock ||
              (isPastDate(property.groupEventDetails.groupBlock?.groupContract?.timespans?.[0]?.start) &&
                isPastDate(property.groupEventDetails.groupBlock?.groupContract?.timespans?.[0]?.end));

            if (hasPastDateError) {
              setErrorMessages([pastDateErrorDtt]);
            } else if (property?.groupEventDetails) {
              const { groupBlock } = property.groupEventDetails;
              const { groupContract, products, sourceCRS, inventoryStatistics } = groupBlock;
              const { groupByGroupPeriods, groupByProductPeriods } = inventoryStatistics || {};

              const isHousingProtected = groupContract?.derivedFields?.isHousingProtected;

              if (!isHousingProtected) {
                const startDate = groupContract?.timespans?.[0]?.start;
                const endDate = groupContract?.timespans?.[0]?.end;
                const groupBlockCutOffDate = groupContract?.releaseInventory?.releaseInfo?.date
                  ? formatUTCDateString(groupContract.releaseInventory.releaseInfo.date)
                  : undefined;
                const pickupAlertIdentityKey = buildPickupAlertIdentityKey(
                  property?.property?.id?.toString(),
                  property?.groupBlockCode,
                  startDate,
                  endDate
                );

                const eventData = getEventTrackingDataDtt(
                  property?.property?.basicInformation?.name || '',
                  index + 1,
                  groupByGroupPeriods,
                  groupContract?.pricePlans?.[0]?.priceGridDetails?.amountRules,
                  products,
                  groupByProductPeriods,
                  property?.groupBlockCode,
                  sourceCRS,
                  property?.property?.media,
                  {
                    pickupAlertIdentityKey,
                    isPickupAlertEligible: true,
                    groupBlockCutOffDate,
                  },
                  startDate
                );

                addOriginalHotelIdentityKey(pickupAlertIdentityKey);
                setEventTrackingData({
                  ...eventData,
                  media: property?.property?.media,
                });
              }
            }
          }
        });

        setValue('propertyCards', propertyCards);
        setEventTrackingPropertyIndex(hotelGroupEventDetails?.length || 0);
      } else {
        router.push(invalidPagePath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  useEffect(() => {
    if (isAlertsView) {
      pickupStatsRef.current?.focus();

      window.scrollTo(0, 0);
    }
  }, [isAlertsView]);

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ReslinkEventDetailsFormData>({
    defaultValues: {
      domain: {
        label: '',
        value: '',
      },
      associates: '',
      eventName: '',
      customImages: [],
      eventDescription: '',
      eventLink1: '',
      eventLinkName1: '',
      eventLink2: '',
      eventLinkName2: '',
      propertyCards: [],
      tnc: false,
    },
    reValidateMode: 'onSubmit',
  });

  const [submitReslink, { loading: submitReslinkLoading, error: submitReslinkError }] =
    useMutation<SubmitReslinkDttResponse>(submitReslinkDttMutation);

  const [createPickupStatsAlertSubscriptionMutation] = useMutation<CreateReslinkPickupStatsAlertSubscriptionResponse>(
    createReslinkPickupStatsAlertSubscriptionDttMutation
  );

  const [updatePickupStatsAlertSubscriptionMutation] = useMutation<UpdateReslinkPickupStatsAlertSubscriptionResponse>(
    updateReslinkPickupStatsAlertSubscriptionDttMutation
  );

  const [deletePickupStatsAlertSubscriptionMutation] = useMutation<DeleteReslinkPickupStatsAlertSubscriptionResponse>(
    deleteReslinkPickupStatsAlertSubscriptionDttMutation
  );

  const imagesModified = useMemo(() => {
    return eventId
      ? getValues('customImages')?.length > 0 &&
          !isEqual(
            eventData?.sales.groupSales.reslinkEventByIdWithInventory.customImageURLs?.map(({ name, link }) => ({
              name: name,
              link,
            })),
            getValues('customImages')
          )
      : getValues('customImages')?.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('customImages')]);

  const closeBackToHubModal = () => {
    setShowBackToHubModal(false);

    requestAnimationFrame(() => {
      backButtonRef.current?.focus();
    });
  };

  const backToReslinkHub = () => {
    resetEventTrackingData();
    router.push(backToRoomingListHubPath);
  };

  const closeTncModal = () => {
    setShowTncModal(false);
  };

  const showTnc = (event: MouseEvent | KeyboardEvent): void => {
    event.preventDefault();
    setShowTncModal(true);
  };

  const createReslinkEvent = (data: ReslinkEventDetailsFormData) => {
    const propertyCards = getValues('propertyCards');
    if (!propertyCards.length) {
      window.scrollTo(0, 0);
      setErrorMessages([requiredError]);
      return;
    }
    setErrorMessages([]);

    const submitReslinkInput = getCreateReslinkEventDttInput(
      isGroup,
      data,
      eventId && `${eventId}_${reslinkType}`,
      isBranchIoAppDownloadEnabled
    );
    const reslinkProduct = `reslink_${reslinkType}` as ReslinkProductType;

    submitReslink({
      variables: { input: submitReslinkInput },
      context: {
        headers: {
          'accept-language': locale,
          'x-request-id': createCustomXRequestId(reslinkProduct, eventId ? 'modify' : 'create', requestId, locale),
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        const { id } = data.sales.groupSales.groupManagement.createOrUpdateReslink;
        if (id) {
          const idTokens = id.split('_');
          resetEventTrackingData();
          router.push({
            pathname: cta.continuePath,
            query: { reslinkType, id: idTokens[0], flow, key: idTokens[1] },
          });
        }
      },
    });
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>, field: { onChange: (value: string) => void }) => {
    field.onChange(event.target.value.trim());
  };

  const validateReslinkEvent = () => {
    if (!getValues('propertyCards').length) {
      window.scrollTo(0, 0);
      setErrorMessages([requiredError]);
    } else if (errors.propertyCards) {
      const propertyCardErrors = (errors.propertyCards as FieldError[])
        .filter(error => error)
        .map(error => error.message as string);
      window.scrollTo(0, 0);
      setErrorMessages([...new Set(propertyCardErrors)]);
    } else {
      setErrorMessages([]);
    }
  };

  const handleAlertCancel = () => {
    window.history.back();
  };

  const getThresholdsFromRecipient = (recipient: AlertsData['recipients'][number]) => {
    const thresholds: number[] = [];

    if (recipient.milestone['25']) thresholds.push(25);
    if (recipient.milestone['50']) thresholds.push(50);
    if (recipient.milestone['75']) thresholds.push(75);
    if (recipient.milestone['90']) thresholds.push(90);

    return thresholds;
  };

  const getSubscriberInput = (alertsData: AlertsData): PickupStatsAlertSubscriberInput[] => {
    return alertsData.recipients.map(recipient => ({
      email: recipient.email.trim().toLowerCase(),
      preferences: {
        daily: recipient.frequency.daily,
        weekly: recipient.frequency.weekly,
        monthly: recipient.frequency.monthly,
        thresholds: getThresholdsFromRecipient(recipient),
      },
      status: PickupStatsAlertStatus.ACTIVE,
    }));
  };

  const createPickupStatsAlertSubscription = async (
    selectedAlertIdentityKey: string,
    subscribers: PickupStatsAlertSubscriberInput[],
    groupBlockCutOffDate?: string
  ) => {
    const [propertyCode, groupBlockCode, groupBlockStartDate, groupBlockEndDate] = selectedAlertIdentityKey.split('|');

    const createReslinkPickupStatsAlertInput: CreateReslinkPickupStatsAlertSubscriptionInput = {
      eventId: `${eventId}_GRP`,
      eventName: eventData?.sales?.groupSales?.reslinkEventByIdWithInventory.eventName,
      eventProduct: PickupStatsAlertEventProduct.RESLINK,
      groupInfo: {
        propertyCode,
        groupBlockCode,
        groupBlockStartDate,
        groupBlockEndDate,
        ...(groupBlockCutOffDate ? { groupBlockCutOffDate } : {}),
      },
      subscribers,
    };

    try {
      const pickupStatsAlertSubscription = await createPickupStatsAlertSubscriptionMutation({
        variables: { input: createReslinkPickupStatsAlertInput },
        context: {
          headers: {
            'accept-language': locale,
            'uxl-cache-disable': true,
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });

      const pickupStatsAlertSubscriptionResponse =
        pickupStatsAlertSubscription.data?.sales?.groupSales?.groupManagement
          ?.createReslinkPickupStatsAlertSubscription;

      if (!pickupStatsAlertSubscriptionResponse || 'errors' in pickupStatsAlertSubscriptionResponse) {
        return { success: false };
      }

      setSyncedPickupStatsAlertSubscriptions(previousState => ({
        ...previousState,
        [selectedAlertIdentityKey]: pickupStatsAlertSubscriptionResponse as PickupStatsAlertSubscription,
      }));

      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const updatePickupStatsAlertSubscription = async (
    selectedAlertIdentityKey: string,
    pickupStatsAlertsData: PickupStatsAlertSubscription,
    subscribers: PickupStatsAlertSubscriberInput[],
    groupBlockCutOffDate?: string
  ) => {
    const { propertyCode, groupBlockCode, groupBlockStartDate, groupBlockEndDate } = pickupStatsAlertsData.groupInfo;

    const updateReslinkPickupStatsAlert: UpdateReslinkPickupStatsAlertSubscriptionInput = {
      eventId: pickupStatsAlertsData.eventId,
      eventName: pickupStatsAlertsData.eventName,
      eventProduct: PickupStatsAlertEventProduct.RESLINK,
      groupInfo: {
        propertyCode,
        groupBlockCode,
        groupBlockStartDate: formatUTCDateString(groupBlockStartDate),
        groupBlockEndDate: formatUTCDateString(groupBlockEndDate),
        ...(groupBlockCutOffDate ? { groupBlockCutOffDate } : {}),
      },
      id: pickupStatsAlertsData.id,
      subscribers,
    };

    try {
      const pickupStatsAlertSubscription = await updatePickupStatsAlertSubscriptionMutation({
        variables: { input: updateReslinkPickupStatsAlert },
        context: {
          headers: {
            'accept-language': locale,
            'uxl-cache-disable': true,
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });

      const pickupStatsAlertSubscriptionResponse =
        pickupStatsAlertSubscription.data?.sales?.groupSales?.groupManagement
          ?.updateReslinkPickupStatsAlertSubscription;

      if (!pickupStatsAlertSubscriptionResponse || 'errors' in pickupStatsAlertSubscriptionResponse) {
        return { success: false };
      }

      setSyncedPickupStatsAlertSubscriptions(previousState => ({
        ...previousState,
        [selectedAlertIdentityKey]: pickupStatsAlertSubscriptionResponse as PickupStatsAlertSubscription,
      }));

      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const deletePickupStatsAlertSubscription = async (
    alertIdentityKey: string,
    existingSubscription: PickupStatsAlertSubscription
  ) => {
    const deleteSubscriptionInput: DeleteReslinkPickupStatsAlertSubscriptionInput = {
      id: existingSubscription.id,
    };

    try {
      const deleteSubscriptionResult = await deletePickupStatsAlertSubscriptionMutation({
        variables: { input: deleteSubscriptionInput },
        context: {
          headers: {
            'accept-language': locale,
            'uxl-cache-disable': true,
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });

      const deleteSubscriptionResponse =
        deleteSubscriptionResult.data?.sales?.groupSales?.groupManagement?.deleteReslinkPickupStatsAlertSubscription;

      if (
        !deleteSubscriptionResponse ||
        'errors' in deleteSubscriptionResponse ||
        !deleteSubscriptionResponse.isDeleted
      ) {
        return { success: false };
      }

      setSyncedPickupStatsAlertSubscriptions(previousState => {
        const updatedState = { ...previousState };
        delete updatedState[alertIdentityKey];
        return updatedState;
      });

      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const handleAlertSave = async (alertsData: AlertsData) => {
    const selectedEventTrackingData = eventTrackingData.find(data => data.propertyIndex === alertPropertyIndex);
    const selectedAlertIdentityKey = selectedEventTrackingData?.pickupAlertIdentityKey;

    if (!selectedAlertIdentityKey) {
      return { success: false };
    }

    const pickupAlertsData = syncedPickupStatsAlertSubscriptions?.[selectedAlertIdentityKey as string];
    const subscribers = getSubscriberInput(alertsData);
    const groupBlockCutOffDate = selectedEventTrackingData?.groupBlockCutOffDate;

    if (pickupAlertsData) {
      if (!subscribers.length) {
        return deletePickupStatsAlertSubscription(selectedAlertIdentityKey, pickupAlertsData);
      }

      return updatePickupStatsAlertSubscription(
        selectedAlertIdentityKey,
        pickupAlertsData,
        subscribers,
        groupBlockCutOffDate
      );
    }

    return createPickupStatsAlertSubscription(selectedAlertIdentityKey, subscribers, groupBlockCutOffDate);
  };

  const handleSetAlert = (propertyIndex: number) => {
    setAlertState({ view: 'alerts', propertyIndex });

    const url = new URL(window.location.href);
    url.searchParams.set('view', 'alerts');
    url.searchParams.set('propertyIndex', String(propertyIndex));
    window.history.pushState(null, '', url.toString());
  };

  const domainField = (
    <Controller
      name="domain"
      control={control}
      rules={{
        validate: (domain: { value: string }) => {
          if (!domain.value) {
            window.scrollTo(0, 0);
            return selectDomain.requiredError;
          }
          return true;
        },
      }}
      render={({ field }) => (
        <Dropdown
          {...field}
          label={selectDomain.label}
          ariaLabel={selectDomain.label}
          isError={!!errors.domain?.message}
          errorMessage={errors.domain?.message}
          expandIconAriaLabel={selectDomain.expandIconAriaLabel}
          collapseIconAriaLabel={selectDomain.collapseIconAriaLabel}
          selectedValue={
            eventId ? eventData?.sales.groupSales.reslinkEventByIdWithInventory.localeURL : defaultDomain?.code
          }
          options={domainOptions}
          helperText={selectDomain.helperText}
          onChange={async (event: DomainOption) => {
            setEmailLocale(event.locale);
            field.onChange(event);
          }}
        />
      )}
    />
  );

  const associatesField = (
    <Controller
      name="associates"
      control={control}
      rules={{
        validate: associates => {
          if (!associates) return true;
          const associatesList = associates
            .split(/[;,]/g)
            .map(a => a.trim())
            .filter(a => a);

          const hasEmail = associatesList.some(associate => EMAIL_REGEX.test(associate));
          if (hasEmail) {
            return addAssociates.emailErrorText;
          }

          if (associatesList.length > 11) {
            return addAssociates.errorText;
          }

          return true;
        },
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={addAssociates.label}
          ariaLabel={addAssociates.label}
          placeholder={addAssociates.supportText}
          showErrorMessage={!!errors.associates?.message}
          errorMessage={errors.associates?.message}
          maxLength={150}
          showHelperText={true}
          helperText={addAssociates.helperText}
          className="m-input-field"
          onChange={event => {
            field.onChange(event);
          }}
        />
      )}
    />
  );

  const eventNameField = (
    <Controller
      name="eventName"
      control={control}
      rules={{
        required: isGroup ? groupOrEventRequiredError : companyOrOrganizationRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={isGroup ? groupOrEventLabel : companyOrOrganizationLabel}
          placeholder={isGroup ? groupOrEventPlaceholder : companyOrOrganizationPlaceholder}
          showErrorMessage={!!errors?.eventName?.message}
          errorMessage={errors?.eventName?.message}
          maxLength={100}
          showHelperText={true}
          helperText={isGroup ? groupOrEventHelperText : companyOrOrganizationHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventDescriptionField = (
    <Controller
      name="eventDescription"
      control={control}
      render={({ field }) => (
        <TextEditor
          {...field}
          maxLength={1000}
          placeholder={informationPlaceholder}
          content={eventData?.sales.groupSales.reslinkEventByIdWithInventory.eventDescription || ''}
          ariaLabel={informationLabel}
          boldAriaLabel={boldAriaLabel}
          italicAriaLabel={italicAriaLabel}
          linkErrorMessage={linkErrorMessage}
          toolbarOptions={editorProperties}
          onChange={(htmlText: string, plainText: string) => {
            field.onChange(htmlText);
            setEventDescriptionModified(!!plainText.trim());
          }}
        />
      )}
    />
  );

  const eventLink1Field = (
    <Controller
      name="eventLink1"
      control={control}
      rules={{
        required: watch('eventLinkName1') && eventLink1RequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink1Label}
          placeholder={eventLink1Placeholder}
          showErrorMessage={!!errors?.eventLink1?.message}
          errorMessage={errors?.eventLink1?.message}
          maxLength={500}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLinkName1Field = (
    <Controller
      name="eventLinkName1"
      control={control}
      rules={{
        required: watch('eventLink1') && eventLink1NameRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink1Name}
          placeholder={eventLink1NamePlaceholder}
          showErrorMessage={!!errors?.eventLinkName1?.message}
          errorMessage={errors?.eventLinkName1?.message}
          maxLength={40}
          showHelperText={true}
          helperText={eventLink1NameHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLink2Field = (
    <Controller
      name="eventLink2"
      control={control}
      rules={{
        required: watch('eventLinkName2') && eventLink2RequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink2Label}
          placeholder={eventLink2Placeholder}
          showErrorMessage={!!errors?.eventLink2?.message}
          errorMessage={errors?.eventLink2?.message}
          maxLength={500}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLinkName2Field = (
    <Controller
      name="eventLinkName2"
      control={control}
      rules={{
        required: watch('eventLink2') && eventLink2NameRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink2Name}
          placeholder={eventLink2NamePlaceholder}
          showErrorMessage={!!errors?.eventLinkName2?.message}
          errorMessage={errors?.eventLinkName2?.message}
          maxLength={40}
          showHelperText={true}
          helperText={eventLink2NameHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const tncOption = (
    <Controller
      name="tnc"
      control={control}
      rules={{ required: termsAndConditionPopUp.requiredError }}
      render={({ field }) => (
        <CheckBoxFormField
          name="tnc"
          options={[
            {
              label: (
                <>
                  <span>{termsAndConditionPopUp.acceptAll}</span>
                  <Link
                    text={termsAndConditionPopUp.linkText}
                    ariaLabel={termsAndConditionPopUp.linkText}
                    linkClassName="tnc-link"
                    linkHref=""
                    target="_self"
                    callback={showTnc}
                  />
                </>
              ),
              value: 'yes',
            },
          ]}
          value=""
          className="p-2 mr-4 tnc-option"
          colLength={12}
          showErrorMessage={!!errors.tnc}
          errorMessage={errors.tnc?.message || ''}
          onChange={field.onChange}
        />
      )}
    />
  );

  const showError = () => {
    window.scrollTo(0, 0);
    return (
      <div className="py-4">
        <InlineMessages type={InlineMessagesType.Error} title={apiFailure} severity="1" />
      </div>
    );
  };

  const eventTitle = eventData?.sales?.groupSales?.reslinkEventByIdWithInventory?.eventName?.trim();

  return (
    <EditableComponent config={ReslinkEventDetailsConfig} {...props}>
      {!isAlertsView && ((getEventByIdError && !eventData) || submitReslinkError) ? showError() : null}
      {!isAlertsView && errorMessages.length ? (
        <div className="mt-4">
          <InlineMessages title={errorInformationMessage} type={InlineMessagesType.Error} messageList={errorMessages} />
        </div>
      ) : null}
      <StyledReslinkEventDetails data-testid="eventdetails" data-component-name="o-groups-eventdetails">
        {loading && <div className="m-spinner-m loader" />}
        {(!!eventId || isValid) && isAlertsView && (
          <div ref={pickupStatsRef} tabIndex={-1} className="pickup-stats-wrapper">
            <PickupStatsAlerts
              propertyIndex={alertPropertyIndex}
              eventTrackingData={eventTrackingData}
              bookingStatsLabels={eventBookingsDetail}
              groupRatesLabels={groupRates}
              emailNotificationLabels={emailNotification}
              alertSubscriptionsByKey={syncedPickupStatsAlertSubscriptions}
              onCancel={handleAlertCancel}
              onSave={handleAlertSave}
              pickupStatsResponseStatus={pickupAlertQueryStatus}
              apiFailureText={apiFailure}
            />
          </div>
        )}
        {(!eventId || isValid || eventTrackingData.length > 0) && (
          <div className={isAlertsView ? 'd-none' : 'd-block'}>
            <Link
              ref={backButtonRef}
              text={cta.backCtaLabel}
              ariaLabel={cta.backCtaLabel}
              linkClassName="m-link-back-tertiary-button mt-4 back-cta"
              linkHref=""
              target="_self"
              callback={(event: React.FormEvent<HTMLInputElement>) => {
                event.preventDefault();
                setShowBackToHubModal(true);
              }}
            />
            {eventId && (
              <div className="row align-items-center justify-content-between m-0">
                <Heading
                  variation={Types.headingType.title}
                  element={Types.tags.h1}
                  titleText={eventTitle}
                  fontSize={Types.size.medium}
                  customClass="my-5 event-title"
                />
                <Button
                  buttonCopy={cta.disableEventCtaLabel}
                  className="m-button-secondary flex-shrink-0"
                  callback={() => setShowDisableEventModal(true)}
                />
              </div>
            )}

            {isPickUpStatsEnabled && isGroup && eventId && (
              <EventTracking
                heading={eventTrackingHeading}
                bookingStatsLabels={eventBookingsDetail}
                groupRatesLabels={groupRates}
                pickupStatsCardLabels={pickupStatsCardLabels}
                eventTrackingData={eventTrackingData}
                alertSubscriptionsByKey={syncedPickupStatsAlertSubscriptions}
                viewAlerts={handleSetAlert}
              />
            )}
            <div className="row align-items-center justify-content-between m-0">
              <Heading
                variation={Types.headingType.subtitle}
                element={Types.tags.h1}
                titleText={heading}
                fontSize={Types.size.extraLarge}
                customClass="my-5"
              />
            </div>
            {showDisableEventModal && (
              <UpdateEventStateModal
                labels={{ ...disableEventModal, apiFailure }}
                eventData={{ eventId: `${eventId}_${reslinkType}`, eventName: getValues('eventName') || '' }}
                updateEventStateType={UpdateEventStateType.DISABLE}
                showModal={showDisableEventModal}
                closeModal={() => setShowDisableEventModal(false)}
              />
            )}

            <form className="mb-5" onSubmit={handleSubmit(createReslinkEvent, validateReslinkEvent)}>
              <div className="mb-5 event-details">
                <div className="row">
                  <div className="col-md-4 mb-5">{domainField}</div>
                  <div className="col-md-8 mb-5">{associatesField}</div>
                </div>
                <Heading
                  variation={Types.headingType.subtitle}
                  element={Types.tags.h2}
                  titleText={optionalHeading}
                  fontSize={Types.size.extraLarge}
                  customClass="my-5"
                />
                <div className="row">
                  <div className="col-md-4 mb-5">{eventNameField}</div>
                </div>
                <div className="row mx-0">
                  <ImageTray
                    {...imageTray}
                    images={images}
                    control={control}
                    uploadImageError={uploadImageError}
                    setUploadImageError={setUploadImageError}
                  />
                </div>
                <hr className="mb-5 mt-4" />
                <div className="row">
                  <div className="col mb-5">
                    <p className="t-font-alt-s mt-2">{informationLabel}</p>
                    {eventDescriptionField}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-8 mb-5">{eventLink1Field}</div>
                  <div className="col-md-4 mb-5">{eventLinkName1Field}</div>
                </div>
                <div className="row">
                  <div className="col-md-8 mb-5">{eventLink2Field}</div>
                  <div className="col-md-4 mb-5">{eventLinkName2Field}</div>
                </div>
              </div>

              <AddHotelDtt
                labels={{ ...reslinkAddHotelsModel, maxRoomCardError: maxRoomCardErrorDtt, apiFailure }}
                control={control}
                errors={errors}
                onError={errorMessage => setErrorMessages(errorMessage ? [errorMessage] : [])}
                isPickUpStatsEnabled={isPickUpStatsEnabled && eventId}
              />
              <div className="d-flex justify-content-end my-5">
                {(eventDescriptionModified || imagesModified) && tncOption}
                <Button
                  type={Types.ButtonTypeVariation.Submit}
                  className={clsx('m-button-primary w-100', submitReslinkLoading && 'm-button skeleton-loader')}
                  isDisabled={submitReslinkLoading}
                  callback={() => uploadImageError && setUploadImageError('')}
                >
                  {cta.continueLabel}
                </Button>
              </div>
            </form>

            <Modal
              handleBlur={true}
              role="dialog"
              show={showBackToHubModal}
              popupOpenState={showBackToHubModal}
              onClose={closeBackToHubModal}
              secondaryClassName={'back-to-hub-modal'}
              setPopupOpenState={closeBackToHubModal}
              disableScrollOnBody={true}
            >
              <Modal.Header labelText={backModalHeading} popupHeaderOnCLoseFunc={closeBackToHubModal}></Modal.Header>
              <Modal.Body className="pb-5 pr-5">
                <p>{stepContent}</p>
                <div className="footer">
                  <Button
                    buttonCopy={cancelCtaLabel}
                    className="m-button-secondary mr-2"
                    callback={closeBackToHubModal}
                  />
                  <Button buttonCopy={yesCtaLabel} className="m-button-primary" callback={backToReslinkHub} />
                </div>
              </Modal.Body>
            </Modal>

            <Modal
              handleBlur={true}
              role="dialog"
              show={showTncModal}
              popupOpenState={showTncModal}
              onClose={closeTncModal}
              setPopupOpenState={closeTncModal}
              disableScrollOnBody={true}
            >
              <Modal.Header
                labelText={termsAndConditionPopUp.headerText}
                popupHeaderOnCLoseFunc={closeTncModal}
                closeIconClickTrackValue="no tracking"
              ></Modal.Header>
              <Modal.Body className="pb-5 pr-5">
                <RichText componentId="termsAndConditionsModal" text={termsAndConditionPopUp.richText} />
              </Modal.Body>
            </Modal>
          </div>
        )}
      </StyledReslinkEventDetails>
    </EditableComponent>
  );
};
